// The stats page's SVG / canvas / export helpers: formatSVG, svgToCanvas, dataURLtoFile, contentToCSV,
// canvasToFile, svgToFile. Pure transforms plus
// the two export-submit helpers, which reach their coffee-side dependencies through native webpack
// equivalents (CSRF/meta, storage encode, stat url, question id, hidden-form submit) instead of `page.lib`.
// The coffee page.lib methods of the same names delegate here through window.$STATS.svgExport.

import { getCSRFParam, getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import { Storage } from '@aytm/lib/storage'

import { getStatUrl } from '@aytm/app/stats/lib/util/helpers'
import { submitHiddenForm } from '@aytm/app/stats/lib/util/dom'
import { questionPositionToId } from '@aytm/app/stats/page/components/lib/utils'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// data: URL -> PNG Blob.
export const dataURLtoFile = dataurl => {
  const binary = atob(dataurl.split(',')[1])
  const array = Array.from({ length: binary.length }, (_v, i) => binary.charCodeAt(i))
  return new Blob([new Uint8Array(array)], { type: 'image/png' })
}

// Serialize an <svg> DOM node to a cleaned string: strip Highcharts gradient-url quoting artifacts,
// inline formatting tags, <br>s, and decode the entity escapes canvas/export choke on.
export const formatSVG = svg => {
  if (svg == null) return null

  const code = new XMLSerializer()
    .serializeToString(svg)
    .replace(/(url\(#highcharts-[0-9]+)&quot;/gi, '$1')
    .replace(/<[biu]>/gi, '')
    .replace(/<\/[biu]>/gi, '')
    .replace(/<br\s*\/>/gi, '')
    .replace(/<br\s*>/gi, '')
    .replace(/%27/gi, "'")
    .replace(/&quot;/gi, "'")
    .replace(/&nbsp;/gi, ' ')

  return code || null
}

// Rasterize an SVG string onto a <canvas>. Resolves null if the image fails to load.
export const svgToCanvas = (svgData, imageW, imageH, fontFamily) =>
  new Promise(resolve => {
    const svg64 = btoa(unescape(encodeURIComponent(svgData)))
    const image64 = 'data:image/svg+xml;base64,' + svg64

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      // Defer a tick so the decoded image is painted before we draw it.
      setTimeout(() => {
        const canvas = document.createElement('canvas')
        canvas.width = imageW
        canvas.height = imageH

        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (fontFamily != null) ctx.font = fontFamily
        ctx.drawImage(img, 0, 0)

        resolve(canvas)
      }, 0)
    }
    img.onerror = () => resolve(null)

    img.src = image64
  })

// Download arbitrary content as a dated CSV named after the survey.
export const contentToCSV = (content, fileNamePart, callback) => {
  const today = new Date()
  const fileName = `survey-${PageGlobals.SURVEY_ID}-${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(today.getDate()).padStart(2, '0')}-${fileNamePart}.csv`

  const blob = new window.Blob([content], { type: 'text/csv;encoding:utf-8' })

  if (window.navigator.msSaveBlob !== undefined) {
    window.navigator.msSaveBlob(blob, fileName)
    if ('function' === typeof callback) callback()
    return
  }

  const blobURL = window.URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = blobURL
  a.style.display = 'none'
  a.setAttribute('download', fileName)
  if (a.download === undefined) {
    a.setAttribute('target', '_blank')
  }
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  setTimeout(() => {
    window.URL.revokeObjectURL(blobURL)
    if ('function' === typeof callback) callback()
  }, 100)
}

// Save a rendered <canvas> as a dated chart file. png/jpeg download client-side; other types round-trip
// through the export_cloud endpoint (server converts, streams the blob back).
export const canvasToFile = (canvas, fileType, position, url) => {
  const today = new Date()

  const arr = position.split(/[._]/)
  let q = questionPositionToId(arr[0]).toLowerCase()
  if (1 < arr.length) {
    q += '-' + arr.slice(1).join('-')
  }
  const fileName = `survey-${PageGlobals.SURVEY_ID}-${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(today.getDate()).padStart(2, '0')}-${q}-chart.${fileType}`

  if (['png', 'jpeg'].includes(fileType)) {
    if (canvas.msToBlob) {
      window.navigator.msSaveBlob(canvas.msToBlob(), fileName)
    } else {
      canvas.toBlob(blob => {
        const URLObj = window.URL || window.webkitURL
        const blobUrl = URLObj.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URLObj.revokeObjectURL(blobUrl)
      }, `image/${fileType}`)
    }
    return
  }

  const exportUrl = getStatUrl(url)

  const data = new FormData()
  data.append('file', dataURLtoFile(canvas.toDataURL('image/png')))
  data.append('file_type', fileType)
  data.append('q', q)
  data.append(getCSRFParam(), getCSRFToken())

  const request = new XMLHttpRequest()
  request.open('POST', exportUrl, true)
  request.responseType = 'blob'
  request.onload = () => {
    if (request.readyState === 4 && request.status === 200) {
      const blob = request.response
      if (typeof window.navigator.msSaveBlob !== 'undefined') {
        window.navigator.msSaveBlob(blob, fileName)
      } else {
        const URLObj = window.URL || window.webkitURL
        const blobURL = URLObj.createObjectURL(blob)
        const tempLink = document.createElement('a')
        tempLink.style.display = 'none'
        tempLink.href = blobURL
        tempLink.setAttribute('download', fileName)
        document.body.appendChild(tempLink)
        tempLink.click()
        document.body.removeChild(tempLink)
        URLObj.revokeObjectURL(blobURL)
      }
    }
  }
  request.send(data)
}

// Ship an SVG string to the export_cloud endpoint via a hidden form (storage-encoded payload + CSRF).
export const svgToFile = (attrs, target = 'iframeSvgDownload', callback) => {
  const meta = {
    file_type: attrs.file_type,
    svg_code: '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + attrs.svg
  }

  if (Object.hasOwn(attrs, 'position')) {
    const arr = attrs.position.split(/[._]/)
    meta.q = questionPositionToId(arr[0]).toLowerCase()
    if (1 < arr.length) {
      meta.q += '-' + arr.slice(1).join('-')
    }
  }
  if (Object.hasOwn(attrs, 'zoom')) meta.zoom = attrs.zoom

  meta[getCSRFParam()] = getCSRFToken()

  // returned: the coffee blocks chain their own callback on it, as they did on page.lib's version
  return Storage.Encode(meta.svg_code)
    .then(svg => {
      meta.svg_code = svg
      const exportUrl = getStatUrl('export_cloud')
      submitHiddenForm(`${exportUrl}?${Math.random()}`, meta, {}, target)
      if ('function' === typeof callback) callback()
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Promise interrupted:', error)
    })
}
